import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
    addCustomer,
    fetchCustomerById,
    updateCustomer,
} from "../../features/customer/CustomerServices";

import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";

import PhoneInput from "react-phone-number-input";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export default function AddCustomer() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    // If customerId is present in the URL, we're in edit mode
    const { customerId } = useParams<{ customerId?: string }>();
    const isEditMode = Boolean(customerId);

    // Form state
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [gender, setGender] = useState<"Male" | "Female" | "Other">("Male");
    const [city, setCity] = useState("");
    const [status, setStatus] = useState<0 | 1>(1);

    const [fetching, setFetching] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // On mount in edit mode — fetch existing customer and pre-fill form
    useEffect(() => {
        if (!isEditMode || !customerId) return;

        const loadCustomer = async () => {
            setFetching(true);
            try {
                const customer = await dispatch(
                    fetchCustomerById(Number(customerId))
                ).unwrap();

                setFullName(customer.fullName ?? "");
                setEmail(customer.email ?? "");
                setMobile(customer.mobile ?? "");
                setGender(customer.gender ?? "Male");
                setCity(customer.city ?? "");
                setStatus(customer.status ?? 1);
            } catch (err: any) {
                toast.error(err?.message || "Failed to load customer");
                navigate(-1);
            } finally {
                setFetching(false);
            }
        };

        loadCustomer();
    }, [customerId, isEditMode]);

    const resetForm = () => {
        setFullName("");
        setEmail("");
        setMobile("");
        setGender("Male");
        setCity("");
        setStatus(1);
    };

    const handleSubmit = async () => {
        if (!fullName || !email || !mobile || !gender || !city) {
            toast.error("Please fill all fields");
            return;
        }

        setSubmitting(true);

        try {
            const formattedMobile = mobile.replace(/\D/g, "");

            const payload = {
                fullName,
                email,
                mobile: formattedMobile,
                gender,
                city,
                status,
            };

            if (isEditMode && customerId) {
                // UPDATE existing customer
                await dispatch(
                    updateCustomer({ id: Number(customerId), ...payload })
                ).unwrap();
                toast.success("Customer updated successfully!");
                navigate(-1);
            } else {
                // ADD new customer
                await dispatch(addCustomer(payload)).unwrap();
                toast.success("Customer added successfully!");
                resetForm();
            }
        } catch (err: any) {
            console.error("Failed to save customer:", err);
            toast.error(err?.message || `Failed to ${isEditMode ? "update" : "add"} customer`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <IoArrowBack
                            className="text-2xl cursor-pointer hover:text-brand-main transition"
                            onClick={() => navigate(-1)}
                        />
                        <span className="font-bold text-lg uppercase">
                            {isEditMode ? "Edit Customer" : "Add Customer"}
                        </span>
                    </div>
                </CustomNavbar>
            }
        >
            {fetching ? (
                // Show a subtle loader while pre-filling data in edit mode
                <div className="bg-white rounded-4xl p-7 flex items-center justify-center min-h-[300px]">
                    <span className="text-gray-400 animate-pulse text-sm">
                        Loading customer data...
                    </span>
                </div>
            ) : (
                <div className="bg-white rounded-4xl p-7 flex flex-col gap-8">
                    {/* SECTION 1 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Full Name */}
                        <div className="flex flex-col gap-2">
                            <label className="font-bold">Full Name</label>
                            <input
                                type="text"
                                placeholder="Enter the full name of your customer"
                                value={fullName}
                                onChange={(e) => {
                                    const cleanedValue = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                    setFullName(cleanedValue);
                                }} className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                            />
                        </div>

                        {/* Email */}
                        <div className="flex flex-col gap-2">
                            <label className="font-bold">Email</label>
                            <input
                                type="email"
                                placeholder="Enter the email address of your customer"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                            />
                        </div>
                    </div>

                    {/* SECTION 2 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Mobile Number */}
                        <div className="flex flex-col gap-2">
                            <label className="font-bold">Mobile Number</label>
                            <div className="border rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-brand-main">
                                <PhoneInput
                                    defaultCountry="IN"
                                    international
                                    withCountryCallingCode
                                    countryCallingCodeEditable={false}
                                    value={mobile}
                                    onChange={(value) => setMobile(value || "")}
                                    placeholder="Enter mobile number"
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Gender */}
                        <div className="flex flex-col gap-2">
                            <label className="font-bold">Gender</label>
                            <select
                                value={gender}
                                onChange={(e) =>
                                    setGender(e.target.value as "Male" | "Female" | "Other")
                                }
                                className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                            >
                                <option>Male</option>
                                <option>Female</option>
                                <option>Other</option>
                            </select>
                        </div>

                        {/* City */}
                        <div className="flex flex-col gap-2">
                            <label className="font-bold">City</label>
                            <input
                                type="text"
                                placeholder="Enter customer city"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                            />
                        </div>

                        {/* Status */}
                        <div className="flex flex-col gap-2">
                            <label className="font-bold">Status</label>
                            <select
                                value={status}
                                onChange={(e) =>
                                    setStatus(Number(e.target.value) as 0 | 1)
                                }
                                className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex sm:justify-end mt-4 sm:mt-20 w-full">
                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="w-full sm:w-auto px-6 py-2 bg-brand-blue-gradient text-white font-semibold whitespace-nowrap text-lg rounded-xl shadow hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting
                                ? isEditMode
                                    ? "Updating..."
                                    : "Adding..."
                                : isEditMode
                                    ? "Update Customer"
                                    : "Add Customer"}
                        </button>
                    </div>
                </div>
            )}
        </PageLayout>
    );
}
